'use client'

import { cn } from '@brew/ui/cn'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { useMediaQuery } from '@/hooks/use-media-query'
import {
  BREW_LOGO_BOX_CLASSNAME,
  BREW_LOGO_FILL_CLASSNAME,
} from './brew-logo-chrome'

export type BrewAnimatedLogoProps = {
  className?: string
}

const BrewAnimatedLogoRive = dynamic(
  () =>
    import('./brew-animated-logo-rive').then(
      (module) => module.BrewAnimatedLogoRive
    ),
  {
    ssr: false,
    loading: () => <BrewStaticLogo />,
  }
)

/**
 * Takes no size of its own — the wrapper in `BrewAnimatedLogo` owns the box.
 * That is deliberate: handing a variant its own dimensions is what made the
 * logo shift, so it is not expressible here.
 */
function BrewStaticLogo() {
  return (
    <Image
      src="/logo.svg"
      alt="Brew"
      width={2963}
      height={1015}
      className={cn(BREW_LOGO_FILL_CLASSNAME, 'object-contain object-left')}
    />
  )
}

/**
 * The wordmark with no renderer: the same sized wrapper, the still image
 * inside it, and no `next/dynamic` chunk.
 *
 * `loading.tsx` shells paint this. A shell that mounted `BrewAnimatedLogo`
 * would fetch and initialize a Rive instance on motion-enabled clients only to
 * discard it moments later when the real hero replaced the shell.
 */
export function BrewStaticWordmark({ className }: BrewAnimatedLogoProps) {
  return (
    <span data-brew-logo="" className={cn(BREW_LOGO_BOX_CLASSNAME, className)}>
      <BrewStaticLogo />
    </span>
  )
}

/** Horizontal Brew wordmark — same Rive asset as the signed-in home hero. */
export function BrewAnimatedLogo({ className }: BrewAnimatedLogoProps) {
  // Default to the still image during SSR/hydration. This avoids a motion
  // flash, keeps the Rive chunk off reduced-motion clients entirely, and
  // paints a real wordmark in the server HTML. Now that the wrapper holds
  // the box, which variant is showing has no layout consequence.
  const shouldReduceMotion = useMediaQuery(
    '(prefers-reduced-motion: reduce)',
    true
  )

  return (
    <span data-brew-logo="" className={cn(BREW_LOGO_BOX_CLASSNAME, className)}>
      {shouldReduceMotion ? <BrewStaticLogo /> : <BrewAnimatedLogoRive />}
    </span>
  )
}
